// Record Client Payment wizard.
//
// A record-ONCE flow (modeled on the receipts / vendor-payment wizard):
// capture one bank deposit and walk it through the money categories a
// billing package carries, as EXPLICIT STEPS (Cimon's design, 2026-07-10):
//
//   Step 1 — Payment details        the true bank-statement figure, plus a
//                                   plain-language preview of where it will go
//   Step 2 — Invoices on this       per-item deposit invoices (shown by
//            billing summary        proposal name) + final invoices
//   Step 3 — Design hours &         non-retainer projects: allocate the
//            reimbursements         payment; retainer projects: read-only
//                                   explanation that the retainer already
//                                   paid these (the draw, with date + lines)
//   Step 4 — Refill the retainer    retainer projects only; writes the
//                                   Retainers-page ledger entry on save
//   Step 5 — Confirm                the whole deposit spelled out in
//                                   sentences; any leftover gets an explained
//                                   choice — save as client credit, or leave
//                                   it alone (payment saved at the placed
//                                   amount, bank figure kept in a note)
//
// One wizard run = one bank deposit. Run it again for the next deposit —
// every run only offers what's still open (targets net prior allocations).
//
// The split/validation brain lives in utils.js (splitPaymentTargets,
// buildPaymentSplitPreview, explainPaymentLeftover,
// validatePaymentWizardSplit — all pure, all tested). On confirm the wizard
// calls props.recordClientPayment(payment, allocations) then
// props.onSaved(...) — the SAME contract BankDepositModal uses, so the
// caller's cascade dispatcher (onBankDepositSaved →
// syncInvoiceStatusFromAllocations → cascadePaidItems) fires the
// deposit→item→PO cascade with no duplicated financial logic here. The
// retainer ledger entry and any client-credit entry are companion writes
// performed AFTER the payment commits; a companion failure keeps the wizard
// open with a loud, specific error and a retry that never re-records the
// payment (phased submit — see submit()).
//
// props (beyond the original contract):
//   retainer            null | {
//                         requested     number — the summary's retainer ask
//                         received      number — replenishment already received
//                         drawAmount    number|null — the draw that covered the
//                                       period's services (retainer_ledger)
//                         drawDate      string|null — the draw's entry date
//                         drawLines     [{ label, amount }] — what the draw paid
//                       }  — non-null exactly when the project has a retainer
//   alreadyPaidLines    [{ label, amount }] — package documents carrying
//                       payments from BEFORE this deposit; explains leftovers
//   creditToPlace       number — client credit applied on the statement
//                       that has not yet been placed on its documents
//                       (resolveBillingPackage.creditToPlace). Folded into
//                       the money the wizard places and recorded as its own
//                       CLIENT_CREDIT_PAYMENT_METHOD payment on submit
//                       (2026-09-27: the 26-Hoback August $14,206.56 credit
//                       left every FIN short by exactly the credit).
//   monthYear           string — e.g. "May 2026" (ledger references)
//   saveRetainerEntry   (entry) => entry — AppContext optimistic saver
//   saveClientCreditEntry (entry) => Promise<entry|null>
//   (see git history for the original prop list: packageLabel, clientName,
//    documentContext, targets, pendingDeposits, pdfUrl/pdfLabel, userEmail,
//    recordClientPayment, onSaved, onClose)

import React, { useState, useMemo } from 'react';
import {
  fmtMoney, autoAllocatePayment,
  splitPaymentTargets, buildPaymentSplitPreview,
  explainPaymentLeftover, validatePaymentWizardSplit,
  splitAllocationsByFunding, CLIENT_CREDIT_PAYMENT_METHOD,
} from '../utils';
import { CurrencyInput } from './ui';
import {
  INK_1, INK_2, INK_3, SURFACE,
  ZONE_GREEN, ZONE_FUCHSIA, ALERT_RED_TEXT, ALERT_AMBER_TEXT, HAIRLINE_AMBER,
  HAIRLINE, HAIRLINE_SHELL, HAIRLINE_ROW, HAIRLINE_THICK_INK,
  LINE_ROW, TABULAR,
  SHADOW_MODAL, SCRIM, TYPE_EYEBROW,
} from '../lib/designTokens';

var PAYMENT_METHODS = ['', 'Wire', 'Check', 'ACH', 'Zelle', 'Credit card', 'Other'];

var labelStyle = Object.assign({}, TYPE_EYEBROW, { marginBottom: 4 });
var inputStyle = {
  width: '100%', padding: '8px 10px', border: HAIRLINE_SHELL, borderRadius: 4,
  fontSize: 12, fontFamily: 'inherit', boxSizing: 'border-box', color: INK_1,
};

function longDate(iso) {
  try {
    return new Date(String(iso) + 'T00:00:00')
      .toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  } catch (e) { return iso; }
}
function r2(x) { return Math.round((parseFloat(x) || 0) * 100) / 100; }

export default function RecordClientPaymentWizard(props) {
  var targets = props.targets || [];
  var pendingDeposits = props.pendingDeposits || [];
  var retainer = props.retainer || null;
  var hasRetainer = !!retainer;
  var clientName = props.clientName || 'the client';

  var stepSplit = useMemo(function() { return splitPaymentTargets(targets); }, [targets]);
  var invoiceTargets = stepSplit.invoiceTargets;
  var serviceTargets = stepSplit.serviceTargets;

  // Step ids — the retainer step exists only on retainer projects. Step 3
  // renders for EVERY project (consistency, Cimon 2026-07-10): allocatable
  // on non-retainer projects, an explanation on retainer ones.
  var stepIds = useMemo(function() {
    var ids = ['details', 'invoices', 'services'];
    if (hasRetainer) ids.push('retainer');
    ids.push('confirm');
    return ids;
  }, [hasRetainer]);
  var STEP_NAMES = {
    details: 'Payment details',
    invoices: 'Invoices on this billing summary',
    services: 'Design hours & reimbursements',
    retainer: 'Refill the retainer',
    confirm: 'Confirm',
  };

  var s1 = useState(1);                 var step = s1[0];          var setStep = s1[1];
  var stepId = stepIds[step - 1];
  // Step 1 — payment fields. Amount is a NUMBER (CurrencyInput commits
  // numeric values on blur).
  var as = useState(0);                 var amount = as[0];        var setAmount = as[1];
  var ds = useState(new Date().toISOString().slice(0, 10));
  var date = ds[0];                     var setDate = ds[1];
  var msx = useState('');               var method = msx[0];       var setMethod = msx[1];
  var brs = useState('');               var bankRef = brs[0];      var setBankRef = brs[1];
  var ns = useState('');                var notes = ns[0];         var setNotes = ns[1];
  // Allocation amounts (numbers), keyed by documentType|documentId — shared
  // by the invoices + services steps (each step edits its own targets).
  var alc = useState({});               var allocMap = alc[0];     var setAllocMap = alc[1];
  // Retainer refill amount. null = the step hasn't been visited yet; it
  // prefills on first entry (min of money-in-hand and the open request).
  var rt = useState(null);              var retainerAmount = rt[0]; var setRetainerAmount = rt[1];
  // Leftover choice on Confirm: '' | 'credit' | 'leave'
  var lc = useState('');                var leftoverChoice = lc[0]; var setLeftoverChoice = lc[1];
  // submit state — phased so a companion-write failure can be retried
  // without ever re-recording the payment.
  var bz = useState(false);             var busy = bz[0];          var setBusy = bz[1];
  var er = useState('');                var err = er[0];           var setErr = er[1];
  var sp = useState(null);              var savedPayment = sp[0];  var setSavedPayment = sp[1];
  var scp = useState(null);             var savedCreditPayment = scp[0]; var setSavedCreditPayment = scp[1];
  var rd = useState(false);             var retainerEntryDone = rd[0]; var setRetainerEntryDone = rd[1];

  var amountNum = r2(amount);
  // Client credit applied on the statement, still to be placed. It rides
  // along with the bank figure: every placement step spends bank + credit.
  var creditNum = Math.max(0, r2(props.creditToPlace));
  var fundsNum = r2(amountNum + creditNum);
  var retainerNum = hasRetainer ? Math.max(0, r2(retainerAmount)) : 0;
  var retainerOutstanding = hasRetainer
    ? Math.max(0, r2((parseFloat(retainer.requested) || 0) - (parseFloat(retainer.received) || 0)))
    : 0;

  function keyFor(t) { return t.documentType + '|' + String(t.documentId); }

  var allocations = useMemo(function() {
    return targets
      .map(function(t) {
        var v = parseFloat(allocMap[keyFor(t)]) || 0;
        return {
          documentType: t.documentType, documentId: String(t.documentId),
          amount: r2(v), label: t.label, ref: t.ref || '',
          balance: r2(t.balance),
        };
      })
      .filter(function(a) { return a.amount > 0.005; });
  }, [allocMap, targets]);

  var allocated = r2(allocations.reduce(function(s, a) { return s + a.amount; }, 0));
  var allocatedInvoices = r2(allocations.filter(function(a) { return a.documentType === 'invoice'; })
    .reduce(function(s, a) { return s + a.amount; }, 0));
  var allocatedServices = r2(allocated - allocatedInvoices);
  // Money not yet given a home, as of the CURRENT step. The retainer only
  // counts once its step has been reached (retainerAmount committed).
  var stillInHand = r2(fundsNum - allocated - retainerNum);

  var splitCheck = useMemo(function() {
    return validatePaymentWizardSplit(fundsNum, allocations, targets, retainerNum, leftoverChoice);
  }, [fundsNum, allocations, targets, retainerNum, leftoverChoice]);
  var leftover = splitCheck.leftover;
  var leftoverWhy = useMemo(function() {
    return explainPaymentLeftover(leftover, props.alreadyPaidLines || [], pendingDeposits);
  }, [leftover, props.alreadyPaidLines, pendingDeposits]);

  var pendingDepositsTotal = pendingDeposits.reduce(function(s, d) {
    return s + (parseFloat(d && d.asked) || 0);
  }, 0);
  var preview = useMemo(function() {
    return buildPaymentSplitPreview(fundsNum, targets, retainerOutstanding, pendingDepositsTotal);
  }, [fundsNum, targets, retainerOutstanding, pendingDepositsTotal]);

  // Per-step fill helpers — Apply automatically fills only THIS step's
  // targets, from whatever the deposit still has in hand.
  function autoFillFor(stepTargets) {
    var otherAllocated = allocations
      .filter(function(a) { return !stepTargets.some(function(t) { return keyFor(t) === a.documentType + '|' + a.documentId; }); })
      .reduce(function(s, a) { return s + a.amount; }, 0);
    var available = r2(fundsNum - otherAllocated);
    var allocs = autoAllocatePayment(Math.max(0, available), stepTargets);
    setAllocMap(function(prev) {
      var next = Object.assign({}, prev);
      stepTargets.forEach(function(t) { delete next[keyFor(t)]; });
      allocs.forEach(function(a) { next[a.documentType + '|' + String(a.documentId)] = a.amount; });
      return next;
    });
  }
  function clearFor(stepTargets) {
    setAllocMap(function(prev) {
      var next = Object.assign({}, prev);
      stepTargets.forEach(function(t) { delete next[keyFor(t)]; });
      return next;
    });
  }
  function setTargetAmount(t, num) {
    setAllocMap(function(prev) {
      var n = Object.assign({}, prev);
      n[keyFor(t)] = num;
      return n;
    });
  }
  function toggleTarget(t) {
    var k = keyFor(t);
    var cur = parseFloat(allocMap[k]) || 0;
    if (cur > 0.005) {
      setTargetAmount(t, 0);
    } else {
      var bal = r2(t.balance);
      var room = r2(fundsNum - allocated - (stepId === 'retainer' ? retainerNum : 0));
      var fill = Math.min(Math.max(room, 0), bal);
      setTargetAmount(t, fill > 0.005 ? r2(fill) : bal);
    }
  }

  // Per-step gates. Mid-wizard, money still in hand is EXPECTED (later
  // steps take it) — only over-placing blocks.
  function stepTargetsOverfilled(stepTargets) {
    return allocations.some(function(a) {
      var mine = stepTargets.some(function(t) { return keyFor(t) === a.documentType + '|' + a.documentId; });
      return mine && a.amount > a.balance + 0.005;
    });
  }
  var overSplit = stillInHand < -0.005;
  function canLeaveStep(id) {
    if (id === 'details') return fundsNum > 0 && !!date;
    if (id === 'invoices') return !stepTargetsOverfilled(invoiceTargets) && !overSplit;
    if (id === 'services') return hasRetainer ? true : (!stepTargetsOverfilled(serviceTargets) && !overSplit);
    if (id === 'retainer') return retainerNum >= 0 && !overSplit;
    return true;
  }

  function goForward() {
    if (!canLeaveStep(stepId)) return;
    var nextId = stepIds[step]; // step is 1-based; stepIds[step] is the next one
    if (nextId === 'retainer' && retainerAmount === null) {
      // First visit — prefill with the smaller of money-in-hand and the
      // open request (never negative; she can edit to anything).
      var inHand = Math.max(0, r2(fundsNum - allocated));
      setRetainerAmount(Math.min(inHand, retainerOutstanding));
    }
    setErr('');
    setStep(step + 1);
  }

  // Payment amount actually recorded: the full bank figure — unless the
  // leftover is deliberately left alone, in which case the payment is
  // saved at the placed amount and the note keeps the bank truth.
  // Funding split: the applied credit settles documents first (it was
  // netted from the procurement ask); the bank payment takes the rest.
  var fundingSplit = useMemo(function() {
    return splitAllocationsByFunding(allocations, creditNum);
  }, [allocations, creditNum]);
  var creditPlaced = r2(fundingSplit.credit.reduce(function(s, a) { return s + a.amount; }, 0));
  var recordedTotal = (leftover > 0.005 && leftoverChoice === 'leave')
    ? Math.max(0, r2(allocated + retainerNum - creditPlaced))
    : amountNum;

  function autoNoteLines() {
    var lines = [];
    if (creditPlaced > 0.005) {
      lines.push('Client credit of ' + fmtMoney(creditPlaced) + ' applied on this statement was placed as a separate ' + CLIENT_CREDIT_PAYMENT_METHOD + ' entry.');
    }
    if (retainerNum > 0.005 || leftover > 0.005) {
      var bits = [fmtMoney(allocatedInvoices) + ' invoices'];
      if (allocatedServices > 0.005) bits.push(fmtMoney(allocatedServices) + ' design hours & reimbursements');
      if (retainerNum > 0.005) bits.push(fmtMoney(retainerNum) + ' retainer');
      if (leftover > 0.005 && leftoverChoice === 'credit') bits.push(fmtMoney(leftover) + ' saved as client credit');
      lines.push('Split: ' + bits.join(' · ') + '.');
    }
    if (leftover > 0.005 && leftoverChoice === 'leave') {
      lines.push('Bank deposit was ' + fmtMoney(amountNum) + '; ' + fmtMoney(leftover) + ' not recorded — handling separately.');
    }
    return lines;
  }

  // Phased submit. Phase 1 records the payment + fires the cascade — it
  // runs at most ONCE (savedPayment guards it). Phases 2/3 are the
  // companion writes; a failure keeps the wizard open with a specific
  // error, and pressing the button again retries ONLY what's missing.
  async function submit() {
    if (busy) return;
    if (!splitCheck.ok) { setErr(splitCheck.error); return; }
    setBusy(true); setErr('');
    try {
      if (!props.recordClientPayment) { setErr('Payment recorder not available.'); setBusy(false); return; }
      // Phase 0 — the applied credit, placed on its documents as a
      // credit-funded payment (not cash; the package nets it back out).
      if (creditPlaced > 0.005 && !savedCreditPayment) {
        var creditPayment = {
          clientId:      (props.documentContext || {}).clientId || '',
          projectLabel:  (props.documentContext || {}).projectLabel || '',
          paymentDate:   date,
          amountTotal:   creditPlaced,
          method:        CLIENT_CREDIT_PAYMENT_METHOD,
          bankReference: '',
          reference:     '',
          notes:         'Client credit applied on the ' + (props.monthYear || '') + ' billing summary, placed with the ' + fmtMoney(amountNum) + ' payment received ' + longDate(date) + '.',
          recordedBy:    props.userEmail || '',
        };
        var creditAllocs = fundingSplit.credit.map(function(a) {
          return { documentType: a.documentType, documentId: a.documentId, amount: a.amount };
        });
        var cres = await props.recordClientPayment(creditPayment, creditAllocs);
        if (!cres || !cres.ok) {
          setErr((cres && cres.error && (cres.error.message || cres.error)) || 'Could not place the client credit. Try again.');
          setBusy(false);
          return;
        }
        setSavedCreditPayment(cres.payment);
        if (props.onSaved) await props.onSaved(cres.payment, cres.allocations || []);
      }

      var paymentRec = savedPayment;
      if (!paymentRec && recordedTotal > 0.005) {
        var noteText = [notes.trim()].concat(autoNoteLines()).filter(Boolean).join('\n');
        var payment = {
          clientId:      (props.documentContext || {}).clientId || '',
          projectLabel:  (props.documentContext || {}).projectLabel || '',
          paymentDate:   date,
          amountTotal:   recordedTotal,
          method:        method,
          bankReference: bankRef.trim(),
          reference:     '',
          notes:         noteText,
          recordedBy:    props.userEmail || '',
        };
        var cleanAllocs = fundingSplit.bank.map(function(a) {
          return { documentType: a.documentType, documentId: a.documentId, amount: a.amount };
        });
        var res = await props.recordClientPayment(payment, cleanAllocs);
        if (!res || !res.ok) {
          setErr((res && res.error && (res.error.message || res.error)) || 'Could not record the payment. Try again.');
          setBusy(false);
          return;
        }
        paymentRec = res.payment;
        setSavedPayment(paymentRec);
        if (props.onSaved) await props.onSaved(res.payment, res.allocations || []);
      }

      // Phase 2 — the retainer ledger entry (retainer projects with money
      // placed on the refill step). Stamped with the payment id so the
      // ledger row and the bank deposit reference each other.
      if (retainerNum > 0.005 && !retainerEntryDone) {
        if (!props.saveRetainerEntry) {
          setErr('The payment IS recorded. The retainer entry could not be saved automatically — add it on the Retainers page: ' + fmtMoney(retainerNum) + ' dated ' + longDate(date) + '.');
          setBusy(false);
          return;
        }
        var entry = props.saveRetainerEntry({
          projectLabel: (props.documentContext || {}).projectLabel || '',
          entryDate:    date,
          entryType:    'deposit',
          amount:       retainerNum,
          reference:    'Retainer replenishment' + (props.monthYear ? ' — ' + props.monthYear + ' summary' : '') + (bankRef.trim() ? ' · ' + bankRef.trim() : ''),
          notes:        'Recorded with client payment' + (paymentRec && paymentRec.id ? ' ' + paymentRec.id : ''),
          sourceType:   'client_payment',
          sourceId:     (paymentRec && paymentRec.id) || '',
          createdBy:    props.userEmail || '',
        });
        if (!entry) {
          setErr('The payment IS recorded. The retainer entry could not be saved — press the button to retry, or add it by hand on the Retainers page: ' + fmtMoney(retainerNum) + ' dated ' + longDate(date) + '.');
          setBusy(false);
          return;
        }
        setRetainerEntryDone(true);
      }

      // Phase 3 — leftover saved as a client credit (when chosen).
      if (leftover > 0.005 && leftoverChoice === 'credit') {
        if (!props.saveClientCreditEntry) {
          setErr('The payment IS recorded. The ' + fmtMoney(leftover) + ' credit could not be saved automatically — add it on the Credits page.');
          setBusy(false);
          return;
        }
        var credit = await props.saveClientCreditEntry({
          clientId:  (props.documentContext || {}).clientId || '',
          entryDate: date,
          entryType: 'overpayment',
          amount:    leftover,
          reference: 'Overpayment on ' + (props.monthYear ? props.monthYear + ' ' : '') + 'package',
          notes:     (leftoverWhy || '') + (paymentRec && paymentRec.id ? ' Recorded with client payment ' + paymentRec.id + '.' : ''),
          createdBy: props.userEmail || '',
        });
        if (!credit) {
          setErr('The payment IS recorded' + (retainerNum > 0.005 ? ' and the retainer entry saved' : '') + ', but the ' + fmtMoney(leftover) + ' credit could not be saved — press the button to retry, or add it on the Credits page. (If this keeps failing, the 20260710 credit-ledger migration may not have been run.)');
          setBusy(false);
          return;
        }
      }

      if (props.onClose) props.onClose();
    } catch (e) {
      setErr('Network error: ' + (e && e.message ? e.message : 'unknown') + (savedPayment ? ' — the payment IS recorded; pressing the button retries only the remaining entries.' : ''));
      setBusy(false);
    }
  }

  // ── Stepper ──────────────────────────────────────────────────────────────
  function Stepper() {
    return (
      <div data-testid="payment-wizard-stepper"
        style={{ marginBottom: 16, padding: '8px 10px', background: SURFACE, border: HAIRLINE, borderRadius: 4 }}>
        <div style={Object.assign({}, TYPE_EYEBROW)}>
          Step {step} of {stepIds.length}
        </div>
        <div style={{ fontSize: 12, fontWeight: 500, color: INK_1, marginTop: 2 }}>
          {STEP_NAMES[stepId]}
        </div>
        <div style={{ display: 'flex', gap: 3, marginTop: 6 }}>
          {stepIds.map(function(_id, idx) {
            return (
              <div key={idx}
                style={{ flex: 1, height: 2, background: (idx + 1) <= step ? ZONE_FUCHSIA : LINE_ROW, borderRadius: 1 }} />
            );
          })}
        </div>
      </div>
    );
  }

  // ── Pending-deposit notice (details + invoices steps) ───────────────────
  function PendingDepositNotice() {
    if (pendingDeposits.length === 0) return null;
    return (
      <div data-testid="pending-deposit-notice"
        style={{ marginBottom: 14, padding: '10px 12px', border: HAIRLINE_AMBER, borderRadius: 4, background: SURFACE }}>
        <div style={{ fontSize: 11, fontWeight: 500, color: ALERT_AMBER_TEXT, marginBottom: 4 }}>
          {pendingDeposits.length === 1 ? 'A deposit on this package isn’t payable yet' : 'Some deposits on this package aren’t payable yet'}
        </div>
        {pendingDeposits.map(function(d, i) {
          return (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 11, color: INK_1, padding: '2px 0' }}>
              <span>{d.name} — deposit</span>
              <span style={{ fontFeatureSettings: TABULAR, whiteSpace: 'nowrap' }}>{fmtMoney(d.asked)}</span>
            </div>
          );
        })}
        <div style={{ fontSize: 10.5, color: INK_2, marginTop: 5, lineHeight: 1.5 }}>
          Deposits can take a payment only after the proposal is approved.
          If this payment covers {pendingDeposits.length === 1 ? 'this deposit' : 'these deposits'},
          close this window, approve the proposal on the Proposals tab
          (Approve + Assign SKU), then come back — the deposit will appear
          in the list here.
        </div>
      </div>
    );
  }

  // ── Step 1 — details + split preview ─────────────────────────────────────
  function SplitPreview() {
    if (fundsNum <= 0) return null;
    var rows = [];
    rows.push({
      amount: preview.toInvoices,
      text: 'open invoices on this billing summary',
      hint: 'next step',
    });
    if (!hasRetainer && (preview.toServices > 0.005 || preview.servicesOpen > 0.005)) {
      rows.push({
        amount: preview.toServices,
        text: 'design hours & reimbursements billed on this summary',
        hint: 'Step 3',
      });
    }
    if (hasRetainer && retainerOutstanding > 0.005) {
      rows.push({
        amount: preview.toRetainer,
        text: 'retainer replenishment requested on this summary',
        hint: 'Step 4',
      });
    }
    if (preview.toPendingDeposits > 0.005) {
      var pendNames = pendingDeposits.map(function(d) { return d.name; }).join(', ');
      rows.push({
        amount: preview.toPendingDeposits,
        text: 'deposit' + (pendingDeposits.length > 1 ? 's' : '') + ' on ' + pendNames
          + ' — awaiting proposal approval. Approve the proposal first (Approve + Assign SKU), then this becomes payable in Step 2.',
        hint: '',
      });
    }
    if (preview.leftover > 0.005) {
      rows.push({
        amount: preview.leftover,
        text: 'more than was asked — you’ll decide on the last step (save as a credit, or leave it alone)',
        hint: '',
      });
    }
    return (
      <div data-testid="payment-split-preview"
        style={{ marginTop: 14, padding: '10px 12px', border: HAIRLINE, borderRadius: 4, background: SURFACE }}>
        <div style={{ fontSize: 11, fontWeight: 500, color: INK_1, marginBottom: 6 }}>
          Here’s how this payment lines up with the {props.monthYear ? props.monthYear + ' ' : ''}billing summary:
        </div>
        {rows.map(function(rw, i) {
          return (
            <div key={i} style={{ display: 'flex', gap: 10, fontSize: 11.5, color: INK_1, padding: '3px 0', lineHeight: 1.45 }}>
              <span style={{ fontFeatureSettings: TABULAR, fontWeight: 500, whiteSpace: 'nowrap', minWidth: 86, textAlign: 'right' }}>{fmtMoney(rw.amount)}</span>
              <span style={{ flex: 1, color: INK_2 }}>
                {rw.text}{rw.hint ? <span style={{ color: INK_3 }}> ({rw.hint})</span> : null}
              </span>
            </div>
          );
        })}
      </div>
    );
  }

  function renderDetails() {
    return (
      <div>
        <PendingDepositNotice />
        <div style={{ fontSize: 13, color: INK_1, marginBottom: 14 }}>What did the client send?</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <div style={labelStyle}>Amount received</div>
            <CurrencyInput autoFocus value={amount}
              onValueChange={function(v) { setAmount(v); }}
              style={Object.assign({}, inputStyle, { fontFeatureSettings: TABULAR })} />
            <div style={{ fontSize: 10, color: INK_3, marginTop: 3 }}>The figure on the bank statement — enter the whole thing.</div>
            {creditNum > 0.005 && (
              <div data-testid="credit-to-place" style={{ fontSize: 11, color: INK_1, marginTop: 8, lineHeight: 1.45 }}>
                <span style={{ fontFeatureSettings: TABULAR, fontWeight: 500 }}>+ {fmtMoney(creditNum)}</span>
                <span style={{ color: INK_2 }}> client credit applied on this statement — placed with this payment, so {fmtMoney(fundsNum)} settles the documents.</span>
              </div>
            )}
          </div>
          <div>
            <div style={labelStyle}>Date received</div>
            <input type="date" value={date} onChange={function(e) { setDate(e.target.value); }} style={inputStyle} />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <div style={labelStyle}>Method</div>
            <select value={method} onChange={function(e) { setMethod(e.target.value); }} style={Object.assign({}, inputStyle, { background: SURFACE })}>
              {PAYMENT_METHODS.map(function(m) { return <option key={m} value={m}>{m || '— Pick a method —'}</option>; })}
            </select>
          </div>
          <div>
            <div style={labelStyle}>Bank reference</div>
            <input type="text" value={bankRef} onChange={function(e) { setBankRef(e.target.value); }}
              placeholder="Wire conf # / check # (from the statement)" style={inputStyle} />
          </div>
        </div>
        <div>
          <div style={labelStyle}>Notes (optional)</div>
          <textarea rows={2} value={notes} onChange={function(e) { setNotes(e.target.value); }}
            style={Object.assign({}, inputStyle, { resize: 'vertical' })} />
        </div>
        <SplitPreview />
      </div>
    );
  }

  // ── Allocation list (shared by the invoices + services steps) ────────────
  function AllocationList(p2) {
    var stepTargets = p2.stepTargets;
    return (
      <div style={{ border: HAIRLINE_SHELL, borderRadius: 4, overflow: 'hidden' }}>
        {stepTargets.length === 0 && (
          <div style={{ padding: 14, fontSize: 12, color: INK_3 }}>{p2.emptyText}</div>
        )}
        {stepTargets.map(function(t, idx) {
          var k = keyFor(t);
          var num = parseFloat(allocMap[k]) || 0;
          var checked = num > 0.005;
          var bal = r2(t.balance);
          var partial = checked && num < bal - 0.005;
          return (
            <div key={k} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
              borderTop: idx === 0 ? 'none' : HAIRLINE_ROW,
              background: SURFACE,
              boxShadow: checked ? 'inset 2px 0 0 ' + ZONE_FUCHSIA : 'none',
            }}>
              <input type="checkbox" checked={checked} onChange={function() { toggleTarget(t); }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, color: INK_1 }}>{t.label}</div>
                <div style={{ fontSize: 10, color: INK_3, marginTop: 1, fontFeatureSettings: TABULAR }}>
                  {t.ref ? t.ref + ' · ' : ''}balance {fmtMoney(bal)}
                </div>
                {partial && (
                  <div style={{ fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase', color: ZONE_FUCHSIA, marginTop: 2 }}>
                    Partial — {fmtMoney(r2(bal - num))} stays open
                  </div>
                )}
              </div>
              <CurrencyInput value={num || ''}
                onValueChange={function(v) { setTargetAmount(t, v); }}
                style={{ width: 118, padding: '6px 8px', border: HAIRLINE_SHELL, borderRadius: 4, fontSize: 12, fontFamily: 'inherit', textAlign: 'right', boxSizing: 'border-box', color: INK_1, fontFeatureSettings: TABULAR }} />
            </div>
          );
        })}
      </div>
    );
  }

  function renderInvoices() {
    return (
      <div style={{ flex: 1, minWidth: 300 }}>
        <PendingDepositNotice />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
          <div style={{ fontSize: 13, color: INK_1 }}>
            <span style={{ fontWeight: 500 }}>First, the invoices.</span> Check off each one this payment covers.
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={ghostBtn} onClick={function() { autoFillFor(invoiceTargets); }}>Apply automatically</button>
            <button style={ghostBtn} onClick={function() { clearFor(invoiceTargets); }}>Clear</button>
          </div>
        </div>
        <div style={{ fontSize: 10, color: INK_3, marginBottom: 10 }}>
          Apply automatically fills the oldest balance first, top to bottom. You can adjust any amount by hand.
        </div>
        <AllocationList stepTargets={invoiceTargets}
          emptyText="No open invoices on this billing summary — everything invoiced here is already paid." />
      </div>
    );
  }

  function renderServices() {
    if (!hasRetainer) {
      return (
        <div style={{ flex: 1, minWidth: 300 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
            <div style={{ fontSize: 13, color: INK_1 }}>
              <span style={{ fontWeight: 500 }}>Design hours &amp; reimbursements billed on this summary.</span> Check off what this payment covers.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={ghostBtn} onClick={function() { autoFillFor(serviceTargets); }}>Apply automatically</button>
              <button style={ghostBtn} onClick={function() { clearFor(serviceTargets); }}>Clear</button>
            </div>
          </div>
          <div style={{ marginTop: 10 }}>
            <AllocationList stepTargets={serviceTargets}
              emptyText="Nothing billed for design hours or reimbursements is still open on this package." />
          </div>
        </div>
      );
    }
    // Retainer project — nothing to enter; explain what the retainer covered.
    var lines = (retainer.drawLines || []).filter(function(l) { return l && (parseFloat(l.amount) || 0) > 0.005; });
    var drawTotal = retainer.drawAmount != null
      ? r2(retainer.drawAmount)
      : r2(lines.reduce(function(s, l) { return s + (parseFloat(l.amount) || 0); }, 0));
    return (
      <div data-testid="retainer-draw-panel" style={{ flex: 1, minWidth: 300 }}>
        <div style={{ fontSize: 13, color: INK_1, fontWeight: 500, marginBottom: 6 }}>Already covered by the retainer.</div>
        <div style={{ fontSize: 12, color: INK_2, marginBottom: 10, lineHeight: 1.5 }}>
          When this summary was sent{retainer.drawDate ? ' on ' + longDate(retainer.drawDate) : ''}, these were paid out of {clientName}’s retainer:
        </div>
        <div style={{ border: HAIRLINE_SHELL, borderRadius: 4, overflow: 'hidden' }}>
          {lines.map(function(l, i) {
            return (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12, color: INK_1, padding: '8px 12px', borderTop: i === 0 ? 'none' : HAIRLINE_ROW }}>
                <span>{l.label}</span>
                <span style={{ fontFeatureSettings: TABULAR, whiteSpace: 'nowrap' }}>{fmtMoney(l.amount)}</span>
              </div>
            );
          })}
          {lines.length === 0 && (
            <div style={{ padding: '10px 12px', fontSize: 12, color: INK_2 }}>
              The period’s design hours and project expenses.
            </div>
          )}
          {drawTotal > 0.005 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12, fontWeight: 500, color: INK_1, padding: '9px 12px', borderTop: HAIRLINE_ROW }}>
              <span>Drawn from the retainer</span>
              <span style={{ fontFeatureSettings: TABULAR }}>{fmtMoney(drawTotal)}</span>
            </div>
          )}
        </div>
        <div style={{ fontSize: 11.5, color: INK_2, marginTop: 10, lineHeight: 1.5 }}>
          There’s nothing to enter here — {clientName}’s payment refills the retainer in the next step.
        </div>
      </div>
    );
  }

  function renderRetainer() {
    return (
      <div data-testid="retainer-refill-step" style={{ maxWidth: 420 }}>
        <div style={{ fontSize: 13, color: INK_1, marginBottom: 12, lineHeight: 1.5 }}>
          This summary asked {clientName} to bring the retainer back up.
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: INK_1, padding: '6px 0', borderBottom: HAIRLINE_ROW }}>
          <span>Requested</span>
          <span style={{ fontFeatureSettings: TABULAR, fontWeight: 500 }}>{fmtMoney(retainer.requested)}</span>
        </div>
        {r2(retainer.received) > 0.005 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: INK_2, padding: '6px 0', borderBottom: HAIRLINE_ROW }}>
            <span>Received so far</span>
            <span style={{ fontFeatureSettings: TABULAR }}>{fmtMoney(retainer.received)}</span>
          </div>
        )}
        <div style={{ marginTop: 14 }}>
          <div style={labelStyle}>How much of this payment goes to the retainer?</div>
          <CurrencyInput value={retainerAmount == null ? '' : retainerAmount}
            onValueChange={function(v) { setRetainerAmount(v); }}
            style={Object.assign({}, inputStyle, { fontFeatureSettings: TABULAR, width: 180 })} />
        </div>
        <div style={{ fontSize: 10.5, color: INK_3, marginTop: 8, lineHeight: 1.5 }}>
          This is recorded on the {(props.documentContext || {}).projectLabel || ''} retainer page for you — no separate entry needed.
        </div>
      </div>
    );
  }

  // ── Confirm ──────────────────────────────────────────────────────────────
  function renderConfirm() {
    var partials = allocations.filter(function(a) { return a.amount < a.balance - 0.005; });
    var invoiceCount = allocations.filter(function(a) { return a.documentType === 'invoice'; }).length;
    return (
      <div>
        <div style={{ fontSize: 13, color: INK_1, marginBottom: 14, lineHeight: 1.5 }}>
          You’re recording {props.clientName ? props.clientName + '’s' : 'a'}{' '}
          <span style={{ fontWeight: 500, fontFeatureSettings: TABULAR }}>{fmtMoney(amountNum)}</span>
          {' '}{method ? method.toLowerCase() : 'payment'}, received {longDate(date)}.
          {creditPlaced > 0.005 && (
            <span> The <span style={{ fontWeight: 500, fontFeatureSettings: TABULAR }}>{fmtMoney(creditPlaced)}</span> client credit applied on this statement is placed alongside it as a separate {CLIENT_CREDIT_PAYMENT_METHOD.toLowerCase()} entry.</span>
          )}
        </div>
        {bankRef && <div style={{ fontSize: 11, color: INK_3, marginTop: -8, marginBottom: 12 }}>Bank reference: {bankRef}</div>}

        <div style={Object.assign({}, TYPE_EYEBROW, { marginBottom: 6 })}>Where it goes</div>
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', gap: 10, fontSize: 12, color: INK_1, padding: '4px 0' }}>
            <span style={{ fontFeatureSettings: TABULAR, fontWeight: 500, minWidth: 92, textAlign: 'right' }}>{fmtMoney(allocatedInvoices)}</span>
            <span style={{ color: INK_2 }}>pays {invoiceCount} invoice{invoiceCount === 1 ? '' : 's'} on this billing summary (listed below)</span>
          </div>
          {allocatedServices > 0.005 && (
            <div style={{ display: 'flex', gap: 10, fontSize: 12, color: INK_1, padding: '4px 0' }}>
              <span style={{ fontFeatureSettings: TABULAR, fontWeight: 500, minWidth: 92, textAlign: 'right' }}>{fmtMoney(allocatedServices)}</span>
              <span style={{ color: INK_2 }}>pays design hours &amp; reimbursements billed on this summary</span>
            </div>
          )}
          {retainerNum > 0.005 && (
            <div style={{ display: 'flex', gap: 10, fontSize: 12, color: INK_1, padding: '4px 0' }}>
              <span style={{ fontFeatureSettings: TABULAR, fontWeight: 500, minWidth: 92, textAlign: 'right' }}>{fmtMoney(retainerNum)}</span>
              <span style={{ color: INK_2 }}>refills the retainer (recorded on the retainer page)</span>
            </div>
          )}
        </div>

        {leftover > 0.005 && (
          <div data-testid="leftover-panel"
            style={{ marginBottom: 14, padding: '10px 12px', border: HAIRLINE, borderRadius: 4, background: SURFACE }}>
            <div style={{ fontSize: 12, fontWeight: 500, color: INK_1, marginBottom: 4 }}>
              {fmtMoney(leftover)} left over — here’s why:
            </div>
            <div style={{ fontSize: 11.5, color: INK_2, lineHeight: 1.5, marginBottom: 8 }}>{leftoverWhy}</div>
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 11.5, color: INK_1, padding: '4px 0', cursor: 'pointer' }}>
              <input type="radio" name="leftover-choice" checked={leftoverChoice === 'credit'}
                onChange={function() { setLeftoverChoice('credit'); }} style={{ marginTop: 1 }} />
              <span>
                <span style={{ fontWeight: 500 }}>Save it as a credit on {clientName}’s account</span>
                {' '}— it appears on the Credits page and can be applied to a future billing summary.
              </span>
            </label>
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 11.5, color: INK_1, padding: '4px 0', cursor: 'pointer' }}>
              <input type="radio" name="leftover-choice" checked={leftoverChoice === 'leave'}
                onChange={function() { setLeftoverChoice('leave'); }} style={{ marginTop: 1 }} />
              <span>
                <span style={{ fontWeight: 500 }}>Leave it alone for now</span>
                {' '}— the payment is recorded at {fmtMoney(r2(allocated + retainerNum))}, and a note keeps the full bank figure.
              </span>
            </label>
          </div>
        )}

        <div style={Object.assign({}, TYPE_EYEBROW, { marginBottom: 6 })}>Applied to</div>
        {allocations.map(function(a) {
          var isPartial = a.amount < a.balance - 0.005;
          return (
            <div key={a.documentType + '|' + a.documentId} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12, padding: '6px 0', borderBottom: HAIRLINE_ROW, color: INK_1 }}>
              <span>
                {a.label}
                {a.ref && <span style={{ color: INK_3 }}> · {a.ref}</span>}
                {isPartial && (
                  <span style={{ display: 'block', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase', color: ZONE_FUCHSIA, marginTop: 1 }}>
                    Partial — {fmtMoney(r2(a.balance - a.amount))} stays open
                  </span>
                )}
              </span>
              <span style={{ fontFeatureSettings: TABULAR, whiteSpace: 'nowrap' }}>{fmtMoney(a.amount)}</span>
            </div>
          );
        })}
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 500, paddingTop: 10, color: INK_1 }}>
          <span>Total to invoices{allocatedServices > 0.005 ? ' & services' : ''}</span>
          <span style={{ fontFeatureSettings: TABULAR }}>{fmtMoney(allocated)}</span>
        </div>

        <div style={{ fontSize: 11, color: INK_2, marginTop: 14, lineHeight: 1.6 }}>
          Nothing is saved until you press Record Payment. After that:
          {' '}the invoices above are marked paid{partials.length > 0
            ? ' (' + partials.map(function(a) {
                return a.label + ' keeps ' + fmtMoney(r2(a.balance - a.amount)) + ' open';
              }).join('; ') + ')'
            : ''}, their items move forward so purchase orders can go out
          {retainerNum > 0.005 ? ', the retainer balance updates' : ''}
          {leftover > 0.005 && leftoverChoice === 'credit' ? ', and the ' + fmtMoney(leftover) + ' credit appears on the Credits page' : ''}.
        </div>
      </div>
    );
  }

  var ghostBtn = {
    padding: '5px 10px', border: HAIRLINE_SHELL, borderRadius: 4, background: SURFACE,
    color: INK_1, fontSize: 11, fontFamily: 'inherit', cursor: 'pointer',
  };
  var primaryBtn = function(enabled) {
    return {
      padding: '7px 16px', border: HAIRLINE_THICK_INK, borderRadius: 6,
      background: INK_1, color: SURFACE, opacity: enabled ? 1 : 0.4,
      fontSize: 12, fontWeight: 500, fontFamily: 'inherit',
      cursor: enabled ? 'pointer' : 'not-allowed',
    };
  };

  var atConfirm = stepId === 'confirm';
  var showPdf = !!props.pdfUrl && (stepId === 'invoices' || stepId === 'services');
  var showTally = stepId === 'invoices' || stepId === 'services' || stepId === 'retainer';
  var stepsAhead = !atConfirm && step < stepIds.length - 1;
  var recordEnabled = splitCheck.ok && !busy;
  var recordLabel = busy
    ? 'Recording…'
    : ((savedPayment || savedCreditPayment) ? 'Retry remaining entries' : 'Record Payment');

  return (
    <div style={{ position: 'fixed', inset: 0, background: SCRIM, zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: showPdf ? 1080 : 560, maxWidth: '96vw', maxHeight: '92vh', background: SURFACE, borderRadius: 6, boxShadow: SHADOW_MODAL, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '16px 20px', borderBottom: HAIRLINE_SHELL }}>
          <div>
            <div style={{ fontSize: 14, color: INK_1, fontWeight: 500 }}>Record Client Payment</div>
            {props.packageLabel && <div style={{ fontSize: 11, color: INK_3, marginTop: 2 }}>{props.packageLabel}</div>}
          </div>
          <button style={{ border: 'none', background: 'transparent', color: INK_3, fontSize: 18, lineHeight: 1, cursor: 'pointer' }} onClick={props.onClose} title="Close">×</button>
        </div>

        {/* Body — two panes when the as-sent PDF is available */}
        <div style={{ padding: '18px 20px', overflowY: 'auto', flex: 1, display: 'flex', gap: 20 }}>
          {showPdf && (
            <div style={{ flex: '0 0 46%', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              <div style={Object.assign({}, TYPE_EYEBROW, { marginBottom: 6 })}>
                {props.pdfLabel || 'Billing summary'}
              </div>
              <iframe title="Billing summary as sent" src={props.pdfUrl}
                style={{ flex: 1, minHeight: 420, width: '100%', border: HAIRLINE, borderRadius: 4 }} />
              <div style={{ fontSize: 10, color: INK_3, marginTop: 6 }}>
                Check each payment against what the client was asked to pay.
              </div>
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <Stepper />
            {stepId === 'details' && renderDetails()}
            {stepId === 'invoices' && renderInvoices()}
            {stepId === 'services' && renderServices()}
            {stepId === 'retainer' && renderRetainer()}
            {stepId === 'confirm' && renderConfirm()}
            {err && (
              <div style={{ marginTop: 14, padding: '8px 10px', border: '0.5px solid ' + ALERT_RED_TEXT, borderRadius: 4, fontSize: 11, color: ALERT_RED_TEXT, background: SURFACE }}>{err}</div>
            )}
          </div>
        </div>

        {/* Pinned tally — allocation steps. Money still in hand mid-wizard
            is EXPECTED (later steps take their share), so it reads calm;
            it only turns red when the pieces exceed the payment. */}
        {showTally && (
          <div style={{ padding: '10px 20px', borderTop: HAIRLINE_ROW }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
              <span style={{ color: INK_2 }}>Applied so far</span>
              <span style={{ color: INK_1, fontFeatureSettings: TABULAR }}>{fmtMoney(r2(allocated + (stepId === 'retainer' ? retainerNum : 0)))}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3, fontSize: 12, fontWeight: 500 }}>
              <span style={{ color: INK_2 }}>Still in hand</span>
              {overSplit
                ? <span style={{ color: ALERT_RED_TEXT }}>over by {fmtMoney(-stillInHand)} — reduce an amount</span>
                : stillInHand > 0.005
                  ? <span style={{ color: INK_1, fontFeatureSettings: TABULAR }}>
                      {fmtMoney(stillInHand)}
                      <span style={{ color: INK_3, fontWeight: 400 }}>
                        {stepsAhead ? ' — handled in the next steps' : ' — you’ll choose what to do with this at Confirm'}
                      </span>
                    </span>
                  : <span style={{ color: ZONE_GREEN }}>$0.00 — fully placed ✓</span>}
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '14px 20px', borderTop: HAIRLINE_SHELL }}>
          <button style={Object.assign({}, ghostBtn, { visibility: step > 1 && !savedPayment && !savedCreditPayment ? 'visible' : 'hidden' })}
            onClick={function() { setStep(step - 1); setErr(''); }}>← Back</button>
          {!atConfirm && (
            <button style={primaryBtn(canLeaveStep(stepId))}
              disabled={!canLeaveStep(stepId)}
              onClick={goForward}>
              Continue →
            </button>
          )}
          {atConfirm && (
            <button style={primaryBtn(recordEnabled)} disabled={!recordEnabled}
              onClick={submit}>
              {recordLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
